import _ from 'lodash';
import { FieldErrors, FieldPathByValue, FieldValues, UseFormRegister } from 'react-hook-form';

interface InputSliderProps<TFieldValues extends FieldValues> {
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  name: FieldPathByValue<TFieldValues, boolean | undefined>;
  label?: string;
  suffix?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

const InputSlider = <TFieldValues extends FieldValues>({
  label,
  name,
  register,
  errors,
  suffix,
  disabled = false,
  readOnly = false,
  required = false
}: InputSliderProps<TFieldValues>): JSX.Element => {
  const error = _.get(errors, name)?.message as string;
  const inputId = String(name);

  return (
    <div className="w-full mb-2">
      <div className="flex items-center justify-between gap-4">
        {/* Main label for accessibility */}
        {label && (
          <label htmlFor={inputId} className="block font-medium">
            {label} {required && <span className="text-red-600">*</span>}
          </label>
        )}

        <div className="flex items-center gap-2">
          {/* Wrap input and toggle in the same label */}
          <label
            htmlFor={inputId}
            className={`relative inline-flex items-center cursor-pointer ${
              disabled || readOnly ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
            }`}
          >
            {/* Hidden checkbox input - must be FIRST element for peer classes */}
            <input
              id={inputId}
              type="checkbox"
              className="hidden peer"
              disabled={disabled || readOnly}
              {...register(name, {
                required,
                disabled: disabled
              })}
            />

            {/* Toggle track */}
            <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-green-500 transition-colors duration-200" />

            {/* Toggle thumb */}
            <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 peer-checked:translate-x-5" />
          </label>
          {suffix && <span>{suffix}</span>}
        </div>
      </div>
      {error && <p className="text-red-600 text-sm font-semibold mt-1">{error}</p>}
    </div>
  );
};

export default InputSlider;
